import {
  formatMillisecondsToMonthYear,
  formatNotesWithOverrides,
  formatSpread,
  formatToThreeDecimals,
  formatUstBenchmark,
  orderAmountFormatter,
} from './deal-series.util';
import { formatDateOrBlank } from '../deal-detail/deal-detail.util';
import { ColDef, ICellRendererParams } from '@ag-grid-community/core';
import { getFixedWidthConfig } from '../deals.util';
import {
  MaturityOrder,
  successOrderStatuses,
} from './deal-series.types';
import { getStatusIconConfig } from '../deal-detail/deal-reconfirm.utils';
import { IconCellRenderer } from '../cell-renderers';
import { TaxStatusEnum } from '../deals.types';

export const dealSeriesBorder = '1px solid #505050';

/** Single source of truth for maturity grid and split-order modal column widths. */
export const DEAL_SERIES_COLUMN_WIDTHS = {
  maturity: 80,
  putDate: 95,
  parAmount: 130,
  coupon: 80,
  yield: 80,
  spread: 130,
  price: 105,
  ustSpread: 130,
  ustBenchmark: 160,
  notes: 70,
  orderAmount: 195,
  conditionsSummary: 680,
  orderStatus: 100,
  actions: 60,
} as const;

/** @deprecated use DEAL_SERIES_COLUMN_WIDTHS.conditionsSummary */
export const CONDITIONS_SUMMARY_COLUMN_WIDTH = DEAL_SERIES_COLUMN_WIDTHS.conditionsSummary;

export const lightGreyCell = {
  backgroundColor: 'rgba(135, 155, 166, 0.06)',
  borderBottom: dealSeriesBorder,
  height: '40px',
};

export const orderEntryCellStyle = {
  ...lightGreyCell,
  border: dealSeriesBorder,
  boxSizing: 'border-box',
};

export const warningOrderEntryCellStyle = {
  ...orderEntryCellStyle,
  border: '1px solid #ffb660',
};

export const maturityDateColDef: ColDef = {
  field: 'MATURITY_DATE',
  headerName: 'Maturity',
  ...getFixedWidthConfig(DEAL_SERIES_COLUMN_WIDTHS.maturity),
  cellRenderer: (params) => {
    if (params.node.isRowPinned()) {
      return 'Totals';
    }
    const { month, year } = formatMillisecondsToMonthYear(params.value);
    return `<span>${month}/</span><b>${year}</b>`;
  },
  cellStyle: (params) => {
    if (params.node.isRowPinned()) {
      // Label styles for 'Totals' cell in pinned pottom row
      return {
        fontWeight: 700,
        color: 'var(--rapid-ag-header-foreground-color)',
      };
    } else {
      return {};
    }
  },
};

export const putDateColDef: ColDef = {
  field: 'PUT_DATE',
  headerName: 'Put Date',
  ...getFixedWidthConfig(DEAL_SERIES_COLUMN_WIDTHS.putDate),
  valueFormatter: (params) =>
    params.node?.isRowPinned() ? '' : formatDateOrBlank(params.value),
};

export const maturityAmountColDef: ColDef = {
  field: 'MATURITY_AMOUNT',
  headerClass: 'ag-right-aligned-header',
  cellStyle: {
    textAlign: 'right',
  },
  headerName: 'Par Amount ($M)',
  ...getFixedWidthConfig(DEAL_SERIES_COLUMN_WIDTHS.parAmount),
  valueFormatter: (params) => (params.node?.isRowPinned() ? '' : orderAmountFormatter(params.value)),
};

export const couponColDef: ColDef = {
  field: 'COUPON',
  headerClass: 'ag-right-aligned-header',
  cellStyle: {
    textAlign: 'right',
  },
  headerName: 'Coupon',
  ...getFixedWidthConfig(DEAL_SERIES_COLUMN_WIDTHS.coupon),
  valueFormatter: (params) => (params.node?.isRowPinned() ? '' : formatToThreeDecimals(params.value)),
};

export const yieldColDef: ColDef = {
  field: 'YIELD',
  headerClass: 'ag-right-aligned-header',
  cellStyle: {
    textAlign: 'right',
  },
  headerName: 'Yield',
  ...getFixedWidthConfig(DEAL_SERIES_COLUMN_WIDTHS.yield),
  valueFormatter: (params) => (params.node?.isRowPinned() ? '' : formatToThreeDecimals(params.value)),
};

export const spreadColDef: ColDef = {
  field: 'SPREAD',
  headerClass: 'ag-right-aligned-header',
  cellStyle: {
    textAlign: 'right',
  },
  headerName: 'Spread to MMD',
  headerTooltip: 'Interpolated MMD',
  ...getFixedWidthConfig(DEAL_SERIES_COLUMN_WIDTHS.spread),
  valueFormatter: (params) => (params.node.isRowPinned() ? '' : formatSpread(params.value)),
};

export const ustSpreadColDef: ColDef = {
  field: 'TAX_TREASURY_SPREAD',
  headerClass: 'ag-right-aligned-header',
  cellStyle: {
    textAlign: 'right',
  },
  headerName: 'Spread to UST',
  ...getFixedWidthConfig(DEAL_SERIES_COLUMN_WIDTHS.ustSpread),
  valueFormatter: (params) => (params.node?.isRowPinned() ? '' : formatSpread(params.value)),
};

export const ustBenchmarkColDef: ColDef = {
  colId: 'UST_BENCHMARK',
  headerName: 'UST Benchmark',
  ...getFixedWidthConfig(DEAL_SERIES_COLUMN_WIDTHS.ustBenchmark),
  valueGetter: (params) => {
    if (params.node?.isRowPinned()) {
      return '';
    }
    const order = params.data as MaturityOrder;
    return formatUstBenchmark(order?.TAX_TREASURY_COUPON, order?.TAX_TREASURY_MATURITY_DATE);
  },
};

export type EconomicsColumnDefs = {
  beforePrice: ColDef[];
  afterPrice: ColDef[];
};

/** Spread-to-MMD vs UST Spread/Benchmark columns by series tax status. */
export const getEconomicsColumnDefs = (taxStatus: TaxStatusEnum): EconomicsColumnDefs => {
  if (taxStatus === TaxStatusEnum.TAXABLE) {
    return { beforePrice: [], afterPrice: [ustSpreadColDef, ustBenchmarkColDef] };
  }
  return { beforePrice: [spreadColDef], afterPrice: [] };
};

export const priceColDef: ColDef = {
  field: 'PRICE',
  headerClass: 'ag-right-aligned-header',
  cellStyle: {
    textAlign: 'right',
  },
  headerName: 'Price',
  ...getFixedWidthConfig(DEAL_SERIES_COLUMN_WIDTHS.price),
  valueFormatter: (params) => (params.node?.isRowPinned() ? '' : formatToThreeDecimals(params.value)),
};

/** Minimum width for Conditions Summary pills on one line; also default width before flex grow. */
export const CONDITIONS_SUMMARY_MIN_WIDTH = 720;

export const createNotesColDef = (): ColDef => ({
  field: 'NOTES',
  headerName: 'Notes',
  resizable: true,
  minWidth: DEAL_SERIES_COLUMN_WIDTHS.notes,
  width: DEAL_SERIES_COLUMN_WIDTHS.notes,
  valueGetter: (params) => {
    if (params.node.isRowPinned()) {
      return '';
    }
    const maturityOrder = params.data;
    return formatNotesWithOverrides(maturityOrder);
  },
});

export const conditionsSummaryColDef: ColDef = {
  field: 'allConditions',
  headerName: 'Conditions Summary',
  minWidth: DEAL_SERIES_COLUMN_WIDTHS.conditionsSummary,
  width: CONDITIONS_SUMMARY_MIN_WIDTH,
  flex: 1,
  cellClass: 'conditions-summary-cell',
  cellRendererSelector: (params) => {
    if (params.node.isRowPinned()) {
      return undefined;
    }
    return { component: 'conditionsRenderer' };
  },
};

export const statusColDef: ColDef = {
  field: 'status',
  headerName: '',
  cellRenderer: IconCellRenderer,
  ...getFixedWidthConfig(DEAL_SERIES_COLUMN_WIDTHS.orderStatus),
  cellRendererParams: (params: ICellRendererParams<MaturityOrder>) => getStatusIconConfig(params),
  tooltipValueGetter: (params) => {
    if (params.data.ERROR_MESSAGE) {
      if (params.data.ORDER_STATUS != null && successOrderStatuses.has(params.data.ORDER_STATUS)) {
        return `Warning from Dealer: [ ${params.data.ERROR_MESSAGE} ]`;
      }
      return params.data.ERROR_MESSAGE;
    }
    if (params.data.IS_CONFIRMED === true &&
        params.data.ORDER_STATUS != null &&
        successOrderStatuses.has(params.data.ORDER_STATUS)) {
      return 'Order has been reconfirmed';
    }
    return null;
  },
};
